import React, { useEffect, useState } from 'react';
import { ChevronDownIcon } from 'lucide-react';
import { Header } from '../components/Header';
import { TourDetailHero } from '../components/TourDetailHero';
import { QuickInfoBar } from '../components/QuickInfoBar';
import { TourNavigation } from '../components/TourNavigation';
import { TourDescription } from '../components/TourDescription';
import { StickyTourMap } from '../components/StickyTourMap';
import { Itinerary } from '../components/Itinerary';
import { TourInclusionsBooking } from '../components/TourInclusionsBooking';
import { TourAlbums } from '../components/TourAlbums';
import { CustomerReviews } from '../components/CustomerReviews';
import { RelatedTours } from '../components/RelatedTours';
import { Footer } from '../components/Footer';
interface TourDetailPageProps {
  tourId: string | null;
  onBack: () => void;
}
export function TourDetailPage({
  tourId,
  onBack
}: TourDetailPageProps) {
  const [activeSection, setActiveSection] = useState('description');
  const [activeDay, setActiveDay] = useState(1);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [tourData, setTourData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  // Check viewport size on mount and resize
  useEffect(() => {
    const checkViewport = () => {
      setIsMobile(window.innerWidth < 768);
      setIsTablet(window.innerWidth >= 768 && window.innerWidth < 1024);
    };
    checkViewport();
    window.addEventListener('resize', checkViewport);
    return () => window.removeEventListener('resize', checkViewport);
  }, []);
  // Fetch tour data based on tourId
  useEffect(() => {
    // Simulate fetching tour data
    setLoading(true);
    // In a real app, this would be an API call using the tourId
    setTimeout(() => {
      // Mock data for the tour
      setTourData({
        id: tourId,
        title: 'Swiss Alpine Adventure'
        // Other tour data would be here
      });
      setLoading(false);
    }, 500);
  }, [tourId]);
  const handleDayChange = (day: number) => {
    setActiveDay(day);
  };
  // Show loading state while fetching tour data
  if (loading) {
    return <div className="w-full min-h-screen bg-white flex items-center justify-center" data-id="element-2322">
        <div className="text-center" data-id="element-2323">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" data-id="element-2324"></div>
          <p className="text-gray-600" data-id="element-2325">Loading tour details...</p>
        </div>
      </div>;
  }
  return <div className="w-full min-h-screen bg-white" data-id="element-2326">
      <Header data-id="element-2327" />
      {/* Hero Section */}
      <TourDetailHero data-id="element-2328" />
      {/* Quick Info Bar */}
      <QuickInfoBar data-id="element-2329" />
      {/* Sticky Navigation */}
      <div className="sticky top-0 z-20 bg-white shadow-md" data-id="element-2330">
        <TourNavigation activeSection={activeSection} setActiveSection={setActiveSection} data-id="element-2331" />
      </div>
      {/* Description Section */}
      <div id="description" className="scroll-mt-16" data-id="element-2332">
        <TourDescription data-id="element-2333" />
      </div>
      {/* Itinerary Section with Map */}
      <div id="itinerary" className="scroll-mt-16 py-8" data-id="element-2334">
        <div className="container mx-auto px-4" data-id="element-2335">
          {isMobile ?
        // Mobile layout - Single column
        <>
              <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-2336">
                Tour Itinerary
              </h2>
              <Itinerary data-id="element-2337" />
              <div className="mt-8 p-4 bg-blue-50 rounded-lg text-center" data-id="element-2338">
                <button className="text-blue-600 font-medium" data-id="element-2339">
                  View Full Map
                </button>
              </div>
            </> :
        // Desktop/Tablet layout - Two columns
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6" data-id="element-2340">
              {/* Left column - Map */}
              <div className="lg:col-span-2" data-id="element-2341">
                <StickyTourMap activeDay={activeDay} onDayClick={handleDayChange} data-id="element-2342" />
              </div>
              {/* Right column - Itinerary */}
              <div className="lg:col-span-3" data-id="element-2343">
                <Itinerary data-id="element-2344" />
              </div>
            </div>}
        </div>
      </div>
      {/* Pricing Section */}
      <div id="pricing" className="scroll-mt-16 bg-gray-50 py-8" data-id="element-2345">
        <div className="container mx-auto px-4" data-id="element-2346">
          <div className="mb-8" data-id="element-2347">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-2348">
              Price & Departures
            </h2>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" data-id="element-2349">
              {/* Price & Departures component */}
              <div className="container mx-auto px-4" data-id="element-2350">
                <div className="space-y-4" data-id="element-2351">
                  {/* Integrated into the page directly */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" data-id="element-2352">
                    <div className="p-4 flex flex-col md:flex-row md:items-center md:justify-between" data-id="element-2353">
                      <div className="flex items-center mb-3 md:mb-0" data-id="element-2354">
                        <div className="mr-4" data-id="element-2355">
                          <div className="text-lg font-bold text-gray-900" data-id="element-2356">
                            Mon, Sep 15{' '}
                            <span className="text-gray-500" data-id="element-2357">-Sep 22</span>
                          </div>
                          <div className="text-sm text-gray-600" data-id="element-2358">
                            09CNXIY815/25MU
                          </div>
                        </div>
                        <div className="flex items-center" data-id="element-2359">
                          <div className="w-2 h-2 rounded-full bg-orange-500 mr-2" data-id="element-2360"></div>
                          <span className="text-sm text-orange-600 font-medium" data-id="element-2361">
                            Selling Fast
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between md:justify-end w-full md:w-auto" data-id="element-2362">
                        <div className="flex items-center mr-4" data-id="element-2363">
                          <img src="https://upload.wikimedia.org/wikipedia/commons/c/c9/China_Eastern_Airlines_logo.svg" alt="China Eastern" className="w-6 h-6 mr-2" data-id="element-2364" />
                          <div className="flex flex-col" data-id="element-2365">
                            <div className="text-sm text-gray-600" data-id="element-2366">From:</div>
                            <div className="font-bold text-gray-900" data-id="element-2367">
                              S$1,680
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center" data-id="element-2368">
                          <ChevronDownIcon size={20} className="text-gray-500" data-id="element-2369" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Inclusions & Important Info Section */}
      <TourInclusionsBooking data-id="element-2370" />
      {/* Gallery Section */}
      <div id="gallery" className="scroll-mt-16" data-id="element-2371">
        <TourAlbums data-id="element-2372" />
      </div>
      {/* Reviews Section */}
      <div id="reviews" className="scroll-mt-16" data-id="element-2373">
        <CustomerReviews data-id="element-2374" />
      </div>
      {/* Related Tours Section */}
      <div className="mt-8" data-id="element-2375">
        <RelatedTours data-id="element-2376" />
      </div>
      <Footer data-id="element-2377" />
      {/* Mobile sticky booking bar */}
      {isMobile && <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 z-30" data-id="element-2378">
          <div className="flex items-center justify-between" data-id="element-2379">
            <div data-id="element-2380">
              <div className="text-lg font-bold text-gray-900" data-id="element-2381">
                CHF 1,899
                <span className="text-sm font-normal text-gray-600 ml-1" data-id="element-2382">
                  per person
                </span>
              </div>
              <div className="text-sm text-green-600" data-id="element-2383">24% off</div>
            </div>
            <button className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md transition-colors" data-id="element-2384">
              Book Now
            </button>
          </div>
        </div>}
    </div>;
}